import { OMAuthenticationHelper } from '@/addons/authentication'
import {
  AbortControllerHelper,
  getAxiosConfigs,
  instantiateApiHelper,
  isValidCashIp,
  prepareApiHelpers,
  sessionApi,
  storeDataApi,
  usersApi,
} from '@/addons/axios'
import { ConnectivityChecker } from '@/addons/connectivity'
import { CookieKeys, LanguagesIso, PageContexts } from '@/addons/enums'
import {
  getBaseURL,
  deleteCookie,
  getSid,
  setCookie,
  getCookie,
  isPosEnabled,
} from '@/addons/functions'
import { isNativeOrWebIos } from '@/addons/mobile'
import { FeLogger } from '@/addons/monitoring'
import { Session, SessionData } from '@/api'
import {
  PortalAuthenticationApiFactory,
  SecondFactorAuth,
  SuccessfulAuthentication,
} from '@/api/portale'
import router, { Routes } from '@/router'
import { AxiosResponse } from 'axios'
import { ActionTree } from 'vuex'
import {
  ConfigActions,
  ConfigGetters,
  ConfigMutations,
} from '@/store/configs-store'
import { ContextPageActions } from '@/store/context-page/context-page'
import store, { RootState } from '@/store'
import { TypedActionContext } from '../_types'
import { GetterNames } from './_getters'
import { MutationNames, Mutations } from './_mutations'
import { AuthCallPayload, IUserMetadata, State } from './_state'
import LoaderController from '@/components/loader/LoaderController'
import { AuthenticationRequires2FAError } from './_errors'
import { clearLocalStorage } from '@/addons/persistence'
import { useToastsStore } from '@/abs-core/shared/stores/toasts'
import { IAMHelper } from '@/abs-core/helpers/IAMHelper'
import {
  formatUserRole,
  getUserInfofromIAM,
  getUserInfofromLogin,
  isUserAdmin,
  UserApiV1SessionsDataAttributes,
} from '@/addons/functions/user'
import { CashiersActions, CashiersGetters } from '../cashier'
import { AuthGetters } from '.'

export enum ActionNames {
  RESET_STATE = 'resetState',
  SET_SID = 'setSid',
  GOTOLOGIN = 'goToLoginPage',
  LOGIN = 'login',
  LOGOUT = 'logout',
  AUTHENTICATE = 'authenticate',
  UPDATE_AUTH_STATUS = 'updateAuthStatus',
  FETCH_CASHES = 'fetchCashes',
  SELECT_CASH = 'selectCash',
  START_POST_LOGIN_ACTIONS = 'startPostLoginActions',
  RESET_STORAGE = 'reset-storage',
}

type AugmentedActionContext = TypedActionContext<Mutations, State, RootState>

function is2FAResponse(
  data: SuccessfulAuthentication | SecondFactorAuth,
): data is SecondFactorAuth {
  return 'force_login_sms' in data
}

const TWO_WEEKS = 1000 * 3600 * 24 * 14

function getAuthErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'login-form.clienteling_failed'
}

export interface Actions {
  [ActionNames.RESET_STATE](context: AugmentedActionContext): void
  [ActionNames.SET_SID](context: AugmentedActionContext, sid: string): void
  [ActionNames.GOTOLOGIN](
    context: AugmentedActionContext,
    logoutError?: string,
  ): void
  [ActionNames.LOGIN](
    context: AugmentedActionContext,
    { username, password }: AuthCallPayload,
  ): Promise<void>
  [ActionNames.LOGOUT](
    context: AugmentedActionContext,
    payload?: { logoutError?: string },
  ): Promise<void>
  [ActionNames.AUTHENTICATE](
    context: AugmentedActionContext,
    authApiResponseData: SessionData,
  ): void
  [ActionNames.UPDATE_AUTH_STATUS](
    context: AugmentedActionContext,
    language: keyof typeof LanguagesIso,
  ): Promise<void>
  [ActionNames.FETCH_CASHES](
    context: AugmentedActionContext,
    reloadCashes: boolean,
  ): Promise<void>
  [ActionNames.SELECT_CASH](
    context: AugmentedActionContext,
    selectedCashId: string,
  ): Promise<void>
  [ActionNames.START_POST_LOGIN_ACTIONS](
    context: AugmentedActionContext,
  ): Promise<void>
  [ActionNames.RESET_STORAGE](): Promise<void>
}

export const actions: ActionTree<State, RootState> & Actions = {
  [ActionNames.RESET_STATE](context) {
    context.commit(MutationNames.RESET_STATE)
  },
  [ActionNames.SET_SID](context, sid) {
    context.commit(MutationNames.SID, sid)
  },
  [ActionNames.LOGIN]: async (
    context,
    { username, password, secondFactor, iam },
  ) => {
    const storeSetup = store.getters[ConfigGetters.GET_STORE_SETUP]
    const usePortalLogin = store.getters[ConfigGetters.USE_LOGIN_PORTAL]
    const portalURL = store.getters[ConfigGetters.GET_PORTAL_URL]
    const iamInternalId = iam?.profile?.profile?.internal_id
    const isIAMLogin = !!iam?.access_token
    const oidcConfig = IAMHelper.getConfigs(
      store.getters[ConfigGetters.GET_CLIENTELING_LOGIN],
    )

    let r: AxiosResponse<Session, unknown>
    try {
      if (isIAMLogin) {
        context.commit(MutationNames.SET_IAM, iam)

        const basePath = oidcConfig.tokenExchange
        const baseConfig = getAxiosConfigs()
        const portalApis = PortalAuthenticationApiFactory(baseConfig, basePath)
        const oidcRes = await portalApis.apiV2OidcLoginPost(
          'ws_interface',
          'login',
          'CLIENTELING',
          'JSON',
          'CLIENTELING',
          {
            headers: {
              Authorization: `Bearer ${iam.access_token}`,
            },
          },
        )

        if (!oidcRes.data?.sid) {
          throw new Error('No SID found')
        }

        r = await sessionApi.apiV1SessionsPortalSidPost(oidcRes.data?.sid, {
          data: {
            type: 'session',
            attributes: {
              cod_cassa: context.rootState.configs?.setup?.cash_id,
            },
          },
        })
      } else if (usePortalLogin) {
        // If we have to use the centralized authentication provider,
        // make sure to instantiate the appropriate helpers.
        const basePath = new URL(portalURL).origin
        const configuration = getAxiosConfigs()
        const portalApis = PortalAuthenticationApiFactory(
          configuration,
          basePath,
        )

        const device2FAUUID = await getCookie(CookieKeys.DEVICE_2FA_UUID)

        // Make the actual login request. If we received a 2FA code, we send that insted of the password.
        // Also, make sure to include the UUID assigned to this device during the previous login.
        const res = await portalApis.apiV2Post(
          username,
          secondFactor || password,
          'ws_interface',
          'login',
          'POS',
          'JSON',
          device2FAUUID,
        )

        if (is2FAResponse(res.data)) {
          // If Portale sent user a 2FA code, we have to show the appropriate input
          // field and we can clear any previously saved 2FA-related cookie.
          await deleteCookie(CookieKeys.DEVICE_2FA_UUID)
          throw new AuthenticationRequires2FAError()
        }

        // If something broke, return an error.
        if (res.data.status !== 'OK' || !res.data.payload?.sid) {
          throw new Error(res.data.status)
        }

        const portalSid = res.data.payload.sid
        const portalUUID = res.data.payload.MMFG_2fa_uuid

        // If Portale emitted an UUID for this device, make sure to save it.
        // The duration of the cookie itself is not too important: if Portale decides to
        // untrust this cookie, user will be challenged again with a 2FA request.
        if (portalUUID) {
          await setCookie(CookieKeys.DEVICE_2FA_UUID, portalUUID, TWO_WEEKS)
        }

        // At this point we can make a POST request to Posweb to convert the Portal SID into a Posweb SID
        r = await sessionApi.apiV1SessionsPortalSidPost(portalSid, {
          data: {
            type: 'session',
            attributes: {
              cod_cassa: context.rootState.configs?.setup?.cash_id,
            },
          },
        })
      } else {
        // Since we do not have to use the centralized authentication provider, we will do a local
        // login by making a POST request to the `/sessions` Posweb endpoint.
        r = await sessionApi.apiV1SessionsPost({
          data: {
            type: 'session',
            attributes: {
              username,
              password,
              cod_cassa: context.rootState.configs?.setup?.cash_id,
            },
          },
        })
      }
      await context.dispatch(ActionNames.AUTHENTICATE, r.data.data)
      const currentLanguage = context.rootState.generic.currentLanguage || 'it'
      await context.dispatch(ActionNames.UPDATE_AUTH_STATUS, currentLanguage)
      await context.dispatch(ActionNames.FETCH_CASHES, null)
      await store.dispatch(CashiersActions.FETCH_CASHIERS)

      const isMobile = store.getters[ConfigGetters.IS_MOBILE]

      // If user is on a mobile device, we have to wait to trigger the post
      // login actions as current user has to choose a cash register.
      if (!isMobile) {
        await context.dispatch(ActionNames.START_POST_LOGIN_ACTIONS)
        await router.push(Routes.DASHBOARD)
      }

      if (isIAMLogin) {
        const role = store.getters[AuthGetters.USER_ROLE]
        const cashiers = store.getters[CashiersGetters.LIST]
        const sessionStores = r?.data?.data?.attributes?.stores ?? []

        const isSalesEnabled = isPosEnabled()
        const isInternalIdInCashiers = cashiers.some(
          (cashier) => cashier.venditrice === iamInternalId,
        )
        const isAbstridAdmin = isUserAdmin(formatUserRole(role))
        const isEnabledInStore = !!storeSetup.stores.some((store) => {
          return sessionStores.includes(store.cod_negozio ?? null)
        })

        if (!isAbstridAdmin && !isEnabledInStore) {
          throw new Error('Not enabled in store')
        }

        if (!!iamInternalId && !isInternalIdInCashiers && !isSalesEnabled) {
          throw new Error('Not enabled in cashiers')
        }
      }
    } catch (error) {
      if (isIAMLogin) {
        try {
          await context.dispatch(ActionNames.LOGOUT, {
            logoutError: getAuthErrorMessage(error),
          })
          return
        } catch (logoutError) {
          FeLogger.error(
            'Clienteling logout after login failure failed',
            logoutError,
          )
        }
      }

      throw error
    }
  },
  [ActionNames.LOGOUT]: async (context, payload) => {
    LoaderController.show({
      section: PageContexts.LOGIN,
    })
    // Cancel any pending request and re-Init AbortController
    AbortControllerHelper.abortAndInit()

    const sid = context.state.sid || (await getSid())
    const iam = context.state.iam
    const oidcConfig = iam
      ? IAMHelper.getConfigs(store.getters[ConfigGetters.GET_CLIENTELING_LOGIN])
      : undefined

    if (sid) {
      try {
        await sessionApi.apiV1SessionsSidDelete(sid, {
          // Make sure we do not accidentally cancel this request.
          signal: undefined,
          headers: {
            Authorization: `Bearer ${sid}`,
          },
        })
        await context.dispatch(ActionNames.RESET_STORAGE)
      } catch (error) {
        FeLogger.error(error)
        const { addToastError } = useToastsStore()
        addToastError('logout.error')
      } finally {
        // Since we have a cookie that has proved to be invalid or has become
        // invalid after a logout, make sure to remove it.
        await deleteCookie(CookieKeys.SID)
      }
    }

    if (iam) {
      IAMHelper.clearIAMData()
    }

    try {
      ConnectivityChecker.getInstance().stop()
      OMAuthenticationHelper.getInstance().stop()
    } catch (error) {
      FeLogger.warn(
        `Failed to stop connectivity checks and/ot OM authentication helpers`,
        error,
      )
    }

    await store.dispatch(ContextPageActions.RESET_CONTEXT_PAGE)
    // Reset global state and redirect user to the login page
    await context.dispatch(ActionNames.RESET_STATE)

    try {
      // Restore the base Posweb configuration so the next login does not reuse
      // the cash-specific API base or the previous cash selection.
      await store.dispatch(ConfigActions.RESET_STATE)
      await prepareApiHelpers()
      await store.dispatch(ConfigActions.FETCH_STORE_SETUP)
    } catch (error) {
      FeLogger.warn('Failed to restore configuration after logout', error)
    }

    LoaderController.hide()

    if (iam && oidcConfig) {
      try {
        // Do not override the IAM logout redirect with a local `/login` navigation.
        await IAMHelper.triggerIAMLogout(oidcConfig, payload?.logoutError)
        return
      } catch (error) {
        FeLogger.error('Clienteling IAM logout failed', error)
      }
    }

    await context.dispatch(ActionNames.GOTOLOGIN, payload?.logoutError)
  },
  [ActionNames.GOTOLOGIN]: (_, logoutError?) => {
    const errorParam = logoutError
      ? `?logoutError=${encodeURIComponent(logoutError)}`
      : ''
    window.location.href = getBaseURL() + Routes.LOGIN + errorParam
  },
  [ActionNames.AUTHENTICATE]: (context, authApiResponseData) => {
    context.commit(MutationNames.IS_AUTHENTICATED, true)
    context.commit(MutationNames.SID, authApiResponseData?.id)
    context.commit(MutationNames.USER, authApiResponseData?.attributes)

    const iam = context.state.iam
    let userInfo = getUserInfofromLogin(
      authApiResponseData?.attributes as UserApiV1SessionsDataAttributes,
    ) as IUserMetadata //  authApiResponseData.attributes
    if (iam) {
      userInfo = getUserInfofromIAM(iam)
    }
    context.commit(MutationNames.SET_USER_METADATA, userInfo)
  },
  [ActionNames.UPDATE_AUTH_STATUS]: async (context, language) => {
    const sid = context.state.sid || (await getSid())
    const posLang = LanguagesIso[language]
    const storedIAM = IAMHelper.getIAMData()

    if (sid) {
      const authApiResponse = await sessionApi.apiV1SessionsSidPatch(
        sid,
        {
          data: {
            type: 'session',
            id: sid,
            attributes: {
              language: posLang,
            },
          },
        },
        {
          signal: AbortControllerHelper.controller?.signal,
          headers: {
            Authorization: `Bearer ${sid}`,
          },
        },
      )
      if (authApiResponse.data) {
        const userDetails = await usersApi.apiV1UtentiGet(
          authApiResponse.data.data?.attributes?.username,
        )

        const combinedData = {
          ...authApiResponse.data.data,
          attributes: {
            ...(userDetails?.data?.data?.[0]?.attributes ?? {}),
            ...authApiResponse.data.data?.attributes,
          },
        }

        context.commit(MutationNames.SET_IAM, storedIAM)
        return context.dispatch(ActionNames.AUTHENTICATE, combinedData)
      }
    }
  },
  [ActionNames.FETCH_CASHES]: async (context, reloadCashes = false) => {
    if (context.getters[GetterNames.CASHES]?.length > 0 && !reloadCashes) {
      return Promise.resolve()
    }
    const cashesApiResponse = await storeDataApi.apiV1PoswebCashesGet()
    context.commit(MutationNames.CASHES, cashesApiResponse?.data?.data ?? [])
  },
  [ActionNames.SELECT_CASH]: async (context, selectedCashId) => {
    // This very next line should become redundant when (and if) we change the order of the cash select and login form.
    context.commit(
      `configs/${ConfigMutations.SET_SELECTED_CASH}`,
      selectedCashId,
      {
        root: true,
      },
    )
    await context.dispatch(ActionNames.START_POST_LOGIN_ACTIONS)
  },
  [ActionNames.START_POST_LOGIN_ACTIONS]: async (context) => {
    const selectedCashId = store.getters[ConfigGetters.SELECTED_CASH]
    const isLive = store.getters[ConfigGetters.IS_LIVE_ENVIRONMENT]
    // If this condition is true, user is using a mobile device (such as an iPad)
    if (isNativeOrWebIos() && selectedCashId) {
      // In this case we have to make subsequent requests directly to the selected cash

      const cash = context.state.cashes.find(
        (c) => c.attributes?.cod_cassa === selectedCashId,
      )

      if (cash?.attributes) {
        const { addToastWarn } = useToastsStore()
        const protocol = cash.attributes?.ip_protocol || 'http'
        const ipAddress = cash.attributes.ip_address || ''
        const port = cash.attributes.ip_port || '8000'

        const cashUrl = `${protocol}://${ipAddress}:${port}`

        // If we are in a production environment, we have a valid cash IP and we were not forcefully requested to
        // route all HTTP traffic trough the master cash, we can we can initialize API helpers once again, this
        // time providing the appropriate base url.
        if (
          isLive &&
          isValidCashIp(ipAddress) &&
          !import.meta.env.VITE_ROUTE_THROUGH_MASTER_CASH
        ) {
          if (await ConnectivityChecker.isReachable(cashUrl)) {
            instantiateApiHelper(cashUrl)
            // Make sure to keep track of this URL so that we can reuse it later on if needed.
            store.commit(
              `configs/${ConfigMutations.SET_SELECTED_CASH_URL}`,
              cashUrl,
              { root: true },
            )
          } else {
            addToastWarn('common.unreachable_cash_register')
            return Promise.reject(
              new Error(`Unreachable cash (URL ${cashUrl})`),
            )
          }
        } else {
          addToastWarn('common.invalid_cash_configuration_wrong_ip')
        }
      }
    }
  },
  [ActionNames.RESET_STORAGE]: () => {
    clearLocalStorage()
    sessionStorage.clear()
    return Promise.resolve()
  },
}
